import { UI_LOCALE_OPTIONS, type UiLocale, type UiStrings } from '@/constants/ui-locale'
import { ChatBubbleIcon, GlobeIcon, MoonIcon } from '@/components/Icons'
import { useStore } from '@/store/useStore'

/** The sample each language tile shows, so the choice is legible before it is made. */
const LANGUAGE_SAMPLE: Record<UiLocale, string> = { ja: 'あ', en: 'Aa' }

/** Icon tile in a card header, matching the one on the Backlog card. */
function CardIcon({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-light)] text-[var(--accent)]"
    >
      {children}
    </span>
  )
}

/**
 * One choosable tile: a preview above, and the radio that names it below.
 *
 * The whole tile is the control rather than the dot beside the label — the
 * preview is the thing being chosen, and a target that looks clickable and is
 * not reads as a broken one.
 */
function OptionTile({
  checked,
  label,
  onSelect,
  children,
}: {
  checked: boolean
  label: string
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={[
        'flex w-full flex-col gap-3 rounded-xl border p-3 text-left transition-colors',
        checked
          ? 'border-[var(--accent)] bg-[var(--accent-light)]'
          : 'border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-[var(--text-tertiary)]',
      ].join(' ')}
    >
      {children}
      <span className="flex items-center gap-2">
        <span
          aria-hidden
          className={[
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
            checked ? 'border-[var(--accent)]' : 'border-[var(--text-tertiary)]',
          ].join(' ')}
        >
          {checked && <span className="h-2 w-2 rounded-full bg-[var(--accent)]" />}
        </span>
        <span
          className={[
            'text-sm font-semibold',
            checked ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]',
          ].join(' ')}
        >
          {label}
        </span>
      </span>
    </button>
  )
}

/** Miniature of the app at this theme, drawn rather than screenshotted so it follows the tokens. */
function ThemePreview({ dark }: { dark: boolean }) {
  const surface = dark ? '#131318' : '#ffffff'
  const line = dark ? '#2a2a35' : '#e2e8f0'
  const bar = dark ? '#3730a3' : '#4f46e5'
  return (
    <span
      aria-hidden
      className="block rounded-lg border p-3"
      style={{ background: surface, borderColor: line }}
    >
      <span className="mb-2 flex items-center gap-2">
        <span className="block h-2 w-10 rounded-full" style={{ background: bar }} />
        <span className="block h-2 flex-1 rounded-full" style={{ background: line }} />
      </span>
      <span className="mb-4 block h-2 w-3/4 rounded-full" style={{ background: line }} />
      <span className="block h-2 w-1/2 rounded-full" style={{ background: line }} />
    </span>
  )
}

/**
 * Language and light/dark, on the settings screen rather than in the account
 * menu they used to hide in.
 *
 * Both are per-browser preferences held in the same store, so they belong on
 * one screen — the menu showed them because there was nowhere else to put
 * them, and a reader looking for "where do I change the language" had no
 * reason to open a menu under their own name.
 *
 * @param t Current locale strings, passed rather than read so this renders the
 *   same way under test as it does beside the Backlog card.
 */
export default function AppearanceSettings({ t }: { t: UiStrings }) {
  const uiLocale = useStore((s) => s.uiLocale)
  const setUiLocale = useStore((s) => s.setUiLocale)
  const theme = useStore((s) => s.theme)
  const toggleTheme = useStore((s) => s.toggleTheme)
  // `!== false` rather than a plain read: this setting is newer than the
  // stored object most readers already have, and the offer is on by default.
  const autoJoinSharedRoom = useStore((s) => s.settings.autoJoinSharedRoom) !== false
  const setSettings = useStore((s) => s.setSettings)

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <CardIcon>
            <GlobeIcon className="h-5 w-5" />
          </CardIcon>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-[var(--text-primary)]">
              {t.settingsLanguageTitle}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {t.settingsLanguageDescription}
            </p>
          </div>
        </div>

        <div role="radiogroup" aria-label={t.settingsLanguageTitle} className="mx-auto mt-4 grid max-w-3xl gap-3 sm:grid-cols-2">
          {UI_LOCALE_OPTIONS.map((option) => (
            <OptionTile
              key={option.code}
              checked={uiLocale === option.code}
              label={option.label}
              onSelect={() => setUiLocale(option.code)}
            >
              <span
                aria-hidden
                className="flex h-24 items-center justify-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-3xl font-semibold text-[var(--text-primary)]"
              >
                {LANGUAGE_SAMPLE[option.code]}
              </span>
            </OptionTile>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <CardIcon>
            <MoonIcon className="h-5 w-5" />
          </CardIcon>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-[var(--text-primary)]">
              {t.settingsThemeTitle}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {t.settingsThemeDescription}
            </p>
          </div>
        </div>

        <div role="radiogroup" aria-label={t.settingsThemeTitle} className="mx-auto mt-4 grid max-w-3xl gap-3 sm:grid-cols-2">
          {/* Two radios rather than the menu's single toggle. A toggle has to
              name the theme it would switch TO, which reads as a label for the
              state you are already in; side by side, the checked one is the
              answer. The store only offers a toggle, so pressing the checked
              tile must do nothing rather than flip to the other theme. */}
          {(['light', 'dark'] as const).map((option) => {
            const checked = theme === option
            return (
              <OptionTile
                key={option}
                checked={checked}
                label={option === 'light' ? t.lightMode : t.darkMode}
                onSelect={() => {
                  if (!checked) toggleTheme()
                }}
              >
                <ThemePreview dark={option === 'dark'} />
              </OptionTile>
            )
          })}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <CardIcon>
            <ChatBubbleIcon className="h-5 w-5" />
          </CardIcon>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-[var(--text-primary)]">
              {t.sharedRoomNeverAsk}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {t.sharedRoomNeverAskHint}
            </p>
          </div>
        </div>

        {/* Phrased as the thing being turned off, so the checked box means
            "do not ask" — the label and the state agree either way round, and
            this is the way the reader thinks about it when they come looking
            for the setting. */}
        <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-[var(--text-primary)]">
          <input
            type="checkbox"
            checked={!autoJoinSharedRoom}
            onChange={(event) => setSettings({ autoJoinSharedRoom: !event.target.checked })}
            className="h-4 w-4 accent-[var(--accent)]"
          />
          {t.sharedRoomNeverAsk}
        </label>
      </section>
    </div>
  )
}
